/**
 * Finance · Vendor Payments (Accounts Payable).
 */
import { useMemo, useState } from "react";
import { CreditCard, Sparkles } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { TabBar, Drawer, Field, ListRow } from "../../components/lib.overlay";
import { useDeepLink, useAutoOpen, RelatedLinksBlock } from "../../lib/integration";
import { DocumentActions } from "../../components/DocumentActions";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { linksForVendorPayment } from "../../mocks/entityLinks";
import { VENDOR_PAYMENTS, VendorPayment, VendorPaymentStatus } from "../../mocks/financeData";

const STATUS_META: Record<VendorPaymentStatus, { bg: string; fg: string; label: string }> = {
  queued:   { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)", label: "Queued"   },
  approved: { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)",         label: "Approved" },
  sent:     { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)",    label: "Sent"     },
  cleared:  { bg: "var(--ph-success-tint)", fg: "var(--ph-success)",    label: "Cleared"  },
  "on-hold":{ bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",     label: "On hold"  },
};

const METHOD_LABEL: Record<VendorPayment["method"], string> = {
  wire:     "Wire",
  card:     "Card",
  cash:     "Cash",
  internal: "Internal",
};

export default function VendorPayments() {
  const { q: initialQ, open, clearOpen } = useDeepLink();
  const [tab, setTab] = useState<VendorPaymentStatus | "all">("all");
  const [method, setMethod] = useState<VendorPayment["method"] | "all">("all");
  const [q, setQ] = useState(initialQ);
  const [selected, setSelected] = useState<VendorPayment | null>(null);
  const [creating, setCreating] = useState(false);
  useAutoOpen(open, VENDOR_PAYMENTS, (v) => v.id, setSelected);

  const vendorPaymentSchema: FieldSpec[] = [
    { key: "supplier",       label: "Supplier",           kind: "text",     placeholder: "e.g. Kingdom Ouds · Riyadh" },
    { key: "reference",      label: "Reference / PO #",   kind: "text",     placeholder: "PO-2026-014" },
    { key: "amountEGP",      label: "Amount (EGP)",       kind: "number",   placeholder: "48000" },
    { key: "method",         label: "Method",             kind: "select",
      options: [
        { value: "wire",     label: "Wire"     },
        { value: "card",     label: "Card"     },
        { value: "cash",     label: "Cash"     },
        { value: "internal", label: "Internal" },
      ] },
    { key: "status",         label: "Status",             kind: "select",
      options: [
        { value: "queued",   label: "Queued"   },
        { value: "approved", label: "Approved" },
        { value: "sent",     label: "Sent"     },
        { value: "on-hold",  label: "On hold"  },
      ] },
    { key: "dueRelative",    label: "Due",                kind: "text",     placeholder: "Net 30 / today / 5d" },
    { key: "notes",          label: "Notes",              kind: "textarea", placeholder: "Internal note (optional)" },
  ];

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: VENDOR_PAYMENTS.length };
    VENDOR_PAYMENTS.forEach((v) => { c[v.status] = (c[v.status] || 0) + 1; });
    return c;
  }, []);

  const methodCounts = useMemo(() => {
    const c: Record<string, number> = { all: VENDOR_PAYMENTS.length };
    VENDOR_PAYMENTS.forEach((v) => { c[v.method] = (c[v.method] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => VENDOR_PAYMENTS.filter((v) =>
      (tab === "all" || v.status === tab) &&
      (method === "all" || v.method === method) &&
      (!q ||
        v.vendor.toLowerCase().includes(q.toLowerCase()) ||
        v.reference.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, method, q],
  );

  // Coarse AP aging derived from `dueRelative` copy (mock).
  const buckets = useMemo(() => {
    const b = { today: 0, "1-7": 0, "8-30": 0, "30+": 0, overdue: 0 };
    VENDOR_PAYMENTS.forEach((v) => {
      if (v.status === "cleared" || v.status === "sent") return;
      const s = v.dueRelative.toLowerCase();
      if (s.includes("overdue")) { b.overdue += v.amountEGP; return; }
      if (s === "today") { b.today += v.amountEGP; return; }
      const n = parseInt(s.replace(/[^0-9]/g, ""), 10);
      if (isNaN(n)) return;
      if (n <= 7)      b["1-7"]  += v.amountEGP;
      else if (n <= 30) b["8-30"] += v.amountEGP;
      else              b["30+"]  += v.amountEGP;
    });
    return b;
  }, []);

  const totalPipeline = VENDOR_PAYMENTS
    .filter((v) => v.status !== "cleared")
    .reduce((n, v) => n + v.amountEGP, 0);
  const totalOnHold = VENDOR_PAYMENTS
    .filter((v) => v.status === "on-hold")
    .reduce((n, v) => n + v.amountEGP, 0);
  const totalDueThisWeek = buckets.today + buckets["1-7"] + buckets.overdue;
  const maxBucket = Math.max(1, ...Object.values(buckets));

  return (
    <div className="space-y-6" data-testid="fn-vendors">
      <Card testId="fn-vp-summary">
        <SectionHeader
          eyebrow="Accounts Payable"
          title="Payment pipeline"
          action={<CreditCard size={14} style={{ color: "var(--ph-ink-muted)" }} />}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total pipeline</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>
              EGP {(totalPipeline / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Due this week</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>
              EGP {(totalDueThisWeek / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>On hold</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>
              EGP {(totalOnHold / 1000).toFixed(0)}K
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Approved to pay</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>
              {counts.approved || 0}
            </div>
          </div>
        </div>
      </Card>

      <Card testId="fn-vp-aging">
        <SectionHeader eyebrow="Aging" title="Payables by window" />
        <div className="space-y-2">
          {(["overdue", "today", "1-7", "8-30", "30+"] as const).map((b) => {
            const label =
              b === "overdue" ? "Overdue" :
              b === "today"   ? "Due today" :
              b === "1-7"     ? "Due in 1–7 d" :
              b === "8-30"    ? "Due in 8–30 d" :
                                "Due in 30+ d";
            const fg =
              b === "overdue" ? "var(--ph-danger)" :
              b === "today"   ? "var(--ph-warning)" :
              b === "1-7"     ? "var(--ph-warning)" :
              b === "8-30"    ? "var(--ph-ai)" :
                                "var(--ph-success)";
            const bar = Math.max(2, (buckets[b] / maxBucket) * 100);
            return (
              <div key={b} data-testid={`fn-vp-age-${b}`}
                className="grid items-center gap-3 py-1.5"
                style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{label}</div>
                <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                  <div style={{ width: `${bar}%`, height: "100%", background: fg }} />
                </div>
                <div className="text-sm tabular-nums text-right" style={{ color: fg }}>
                  EGP {(buckets[b] / 1000).toFixed(0)}K
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card testId="fn-vp-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <TextInput
              value={q}
              onChange={setQ}
              placeholder="Search vendor or PO reference…"
              testId="fn-vp-search"
            />
          </div>
          <Button testId="fn-vp-new-btn" onClick={() => setCreating(true)}>+ New payment</Button>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <TabBar<VendorPaymentStatus | "all">
            testId="fn-vp-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",      label: "All",       count: counts.all              },
              { key: "queued",   label: "Queued",    count: counts.queued   || 0 },
              { key: "approved", label: "Approved",  count: counts.approved || 0 },
              { key: "sent",     label: "Sent",      count: counts.sent     || 0 },
              { key: "cleared",  label: "Cleared",   count: counts.cleared  || 0 },
              { key: "on-hold",  label: "On hold",   count: counts["on-hold"] || 0 },
            ]}
          />
          <TabBar<VendorPayment["method"] | "all">
            testId="fn-vp-methods"
            active={method}
            onChange={setMethod}
            tabs={[
              { key: "all",      label: "Any method"                                 },
              { key: "wire",     label: "Wire",     count: methodCounts.wire     || 0 },
              { key: "card",     label: "Card",     count: methodCounts.card     || 0 },
              { key: "cash",     label: "Cash",     count: methodCounts.cash     || 0 },
              { key: "internal", label: "Internal", count: methodCounts.internal || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="fn-vp-list-card">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Queue" title={`${filtered.length} payment${filtered.length === 1 ? "" : "s"}`} />
          <DocumentActions docKey="vendor-invoice" testId="fn-vp-doc-actions" />
        </div>
        <div className="p-2" data-testid="fn-vp-list">
          {filtered.map((v) => {
            const s = STATUS_META[v.status];
            return (
              <ListRow
                key={v.id}
                testId={`fn-vp-${v.id}`}
                onClick={() => setSelected(v)}
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>{v.vendor}</span>
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                      style={{
                        letterSpacing: "0.14em",
                        background: s.bg,
                        color: s.fg,
                        borderRadius: "var(--ph-radius-sm)",
                      }}
                    >
                      {s.label}
                    </span>
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                      style={{
                        letterSpacing: "0.14em",
                        background: "var(--ph-neutral-tint)",
                        color: "var(--ph-ink-muted)",
                        borderRadius: "var(--ph-radius-sm)",
                      }}
                    >
                      {METHOD_LABEL[v.method]}
                    </span>
                  </span>
                }
                sublabel={<>{v.reference} · {v.currency} · {v.dueRelative}{v.approvedBy ? ` · approved by ${v.approvedBy}` : ""}</>}
                trailing={
                  <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                    EGP {v.amountEGP.toLocaleString()}
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer
        open={Boolean(selected)}
        onClose={() => { setSelected(null); clearOpen(); }}
        title="Vendor payment"
        testId="fn-vp-drawer"
        width={480}
      >
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: STATUS_META[selected.status].fg }}>
              {STATUS_META[selected.status].label}
            </div>
            <div
              style={{
                fontFamily: "var(--ph-display-font)",
                fontSize: "1.2rem",
                color: "var(--ph-ink-strong)",
              }}
              className="mt-1 mb-1"
            >
              {selected.vendor}
            </div>
            <div className="text-[11px] font-mono mb-3" style={{ color: "var(--ph-ink-muted)" }}>
              {selected.reference}
            </div>
            {/* Single-record Vendor Invoice / Bill PDF · ORVAN branded */}
            <div className="mb-4 pb-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="vendor-invoice" recordId={selected.id} testId={`fn-vp-single-${selected.id}`} compact />
            </div>
            <Field label="Amount" inline>
              <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                EGP {selected.amountEGP.toLocaleString()}
              </span>
            </Field>
            <Field label="Currency" inline>
              <span className="text-sm">{selected.currency}</span>
            </Field>
            <Field label="Method" inline>
              <span className="text-sm">{METHOD_LABEL[selected.method]}</span>
            </Field>
            <Field label="Due" inline>
              <span className="text-sm">{selected.dueRelative}</span>
            </Field>
            {selected.approvedBy && (
              <Field label="Approved by" inline>
                <span className="text-sm">{selected.approvedBy}</span>
              </Field>
            )}
            <div className="pt-3">
              <div
                className="text-[10px] uppercase tracking-[0.14em] mb-1.5 inline-flex items-center gap-1"
                style={{ color: "var(--ph-ai)" }}
              >
                <Sparkles size={10} /> AI CFO note
              </div>
              <div
                className="text-sm p-3"
                style={{
                  background: "var(--ph-surface)",
                  borderRadius: "var(--ph-radius-md)",
                  color: "var(--ph-ink-default)",
                }}
              >
                {selected.status === "on-hold" ?
                  "Held pending contract review. Estimated exposure if released today matches the amount above." :
                selected.status === "queued" ?
                  "Queued for weekly approval batch. FX at booking; consider hedging if window exceeds 14d." :
                selected.status === "approved" ?
                  "Approved and scheduled. No further action required." :
                selected.status === "sent" ?
                  "Sent · awaiting bank clearance. Typical clearance 1–3 business days." :
                  "Cleared. Cash applied to July payables."}
              </div>
            </div>
            <RelatedLinksBlock links={linksForVendorPayment(selected.id)} testId="fn-vp-related" />
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="fn-vp-hold">Hold</Button>
              <Button testId="fn-vp-approve">Approve payment</Button>
            </div>
          </div>
        )}
      </Drawer>

      <RecordEditor<VendorPayment>
        open={creating}
        onClose={() => setCreating(false)}
        mode="create"
        title="New vendor payment"
        collection="vendor_payments"
        schema={vendorPaymentSchema}
        initial={{ status: "queued", method: "wire", dueRelative: "Net 30" } as Partial<VendorPayment>}
        testId="fn-vp-new-drawer"
      />
    </div>
  );
}
